import { templateParse, templateParseToElement } from "../../logic/templateParse.js";
import type { ColumnData, ColumnDataFilterSubset, ListFilter } from "../../types/ListManagerTypes.js";
import { FilterOperator, FilterType, getDefaultOperatorsForValueType, getOperatorLabel, getOperatorsForFilterType, getOperatorsList } from "../../constants/FilterConstants.js";
import { BaseWebComponent } from "../base-components/base-web-component.js";
import { FilterOperatorsComponent } from "./filter-operators-component.js";
import { FilterInputComponent } from "./filter-input-component.js";
/**
 * classe base per i controlli input filter web components
 * si occupa di gestire il selettore dell'operatore, il campo di input e la logica di base per i filtri
 * non si occupa di gestire il campo : il campo viene gestito in altro modo
 * qui viene passato il subset di ColumnData che serve per i filtri e già contiene il campo
 */
export abstract class FilterBaseComponent extends BaseWebComponent {
    static webComponentName: string = "filter-element-base";
    /** Unique filter ID for form management */
    protected filterId: string = "";
    protected _templateId: string = "";
    protected colData!: ColumnData;
    protected filterOperators!: FilterOperatorsComponent;
    protected filterInput!: FilterInputComponent;
    constructor() {
        super();
        this.filterId = this.createRandomId();
    }
    // =============================
    // Filter-specific properties
    // =============================

    /** Name of the database field this filter applies to */

    protected initialized: boolean = false; // Flag to track if the component is initialized

    get fieldName(): string {
        if (!this.initialized) {
            throw new Error("colData is not initialized");
        }
        return this.colData.name;
    }

    get filterType(): FilterType {
        if (!this.initialized) {
            throw new Error("colData is not initialized");
        }
        return this.colData.filterType || "simple";
    }


    /**
     * metodo chiave per inizializzare il filtro 
     */
    initialize(colData: ColumnData): void {
        this.colData = colData;
        this.initialized = true;
        this.innerHTML = "";
        this.render();

    }

    protected _operator: FilterOperator = '='; // Default operator

    get operator(): FilterOperator {
        return this._operator;
    }

    set operator(value: FilterOperator) {
        this._operator = value;
        this.dispatch('operator-change', { value: value });
        this.dispatchOperatorChange();
    }
    /** Current filter value */
    protected _filterValue: any = "";

    get filterValue(): any {
        return this._filterValue;
    }

    set filterValue(value: any) {
        this._filterValue = value;
        this.dispatch('filter-value-change', { value: value });
        this.dispatchValueChange(); // nuovo standard
    }

    // =============================
    // Abstract methods - Must be implemented by child classes
    // =============================

    /**
     * Generate a ListFilter object from current UI state
     * This is the main output - converts UI input to typed filter objects
     */
    abstract generateFilter(): ListFilter;


    /**
     * Validate current filter value
     * Child classes implement type-specific validation
     */
    abstract validateFilterValue(): boolean;

    protected afterRender() {
        const filterOperatorsSelector = FilterOperatorsComponent.webComponentName;
        const filterOperators = this.querySelector(filterOperatorsSelector);
        if (filterOperators) {
            this.filterOperators = filterOperators as FilterOperatorsComponent;
            this.filterOperators.colData = this.colData;
            if (this.filterOperators.value) {

                this.operator = this.filterOperators.value;
            }

        }
        const filterInput = this.querySelector("filter-input");
        if (filterInput) {
            this.filterInput = filterInput as FilterInputComponent;
            this.filterInput.colData = this.colData;
            if (this.filterInput.value) {
                this.filterValue = this.filterInput.value;
            }
        }
    }
    protected onConnected(): void {
        this.setAttribute("role", "filter-element"); // attribuisce un 'role' che può essere usato dai figli
    }
    protected onPrepareData(data: Record<string, any>): Record<string, any> {
        // Default implementation does nothing
        // Child classes can override to prepare data before rendering
        return data;
    }
    protected render(): void {
        if (this.templateId) {
            const colData = this.colData;
            let templateData: Record<string, any> = {
                ...colData,
                filterId: this.filterId,
                filterType: this.filterType,
            };
            templateData = this.onPrepareData(templateData);
            templateParseToElement(this.templateId, this, templateData);
            this.afterRender();
            this.setupEventListeners();
        }
        else {
            this.innerHTML = `Se vedi questo messaggio, il render di ${(this.constructor as any).webComponentName} non è gestito.`;
        }
    }


    // =============================
    // Event handling - Filter-specific
    // =============================

    /**
     * Dispatch filter-change event instead of form-change
     * Passes the generated ListFilter object
     */
    protected dispatchFilterEvent(eventName: string = 'filter-change'): void {
        if (!this.validateFilterValue()) {
            return; // Don't dispatch if validation fails
        }
        const filter = this.generateFilter();
        this.dispatch(eventName, {
            filter,
            filterId: this.filterId,
            element: this
        });
    }

    protected dispatchOperatorChange(): void {
        this.dispatch('change-operator', {
            operator: this.operator
        });
    }

    protected dispatchValueChange(): void {
        this.dispatch('change-value', {
            value: this.filterValue
        });
    }

    // =============================
    // Setup and lifecycle
    // =============================


    protected setupEventListeners(): void {
        if (this.filterOperators) {
            this.filterOperators.addEventListener("change", (e: Event) => {
                this.operator = (e as CustomEvent).detail.value;
                this.dispatchFilterEvent("change");
            })
        }
        if (this.filterInput) {
            this.filterInput.addEventListener("change-value-input", (e: Event) => {
                this.filterValue = (e as CustomEvent).detail.value;
                this.dispatchFilterEvent("change");
            });
        }
    }

 
}